<script>
	import { tick } from 'svelte';
	import { page } from '$app/stores';
	import { fade, fly } from 'svelte/transition';
	import { CONFIG } from '$lib/config/index.js';

	let menuOpen = false;

	/** @type {HTMLButtonElement|null} */
	let menuButton = null;
	/** @type {HTMLElement|null} */
	let menuPanel = null;

	$: isHomePage = $page.url.pathname === '/';
	$: isSearchPage = $page.url.pathname.startsWith('/search');
	$: pathname = $page.url.pathname;

	// Close the mobile menu whenever navigation happens
	$: (pathname, closeMenu());

	const navItems = [
		{ href: `/issue/${CONFIG.conference.currentIssueId}`, label: 'Current Issue' },
		{ href: '/issues', label: 'All Issues' },
		{ href: '/articles', label: 'Articles' },
		{ href: '/search', label: 'Search' }
	];

	/**
	 * @param {string} href
	 * @param {string} path
	 */
	function isActive(href, path) {
		return path === href || path === `${href}/`;
	}

	async function openMenu() {
		menuOpen = true;
		await tick();
		menuPanel?.querySelector('button')?.focus();
	}

	function closeMenu() {
		if (!menuOpen) return;
		menuOpen = false;
		menuButton?.focus();
	}

	/** @param {KeyboardEvent} event */
	function handleKeydown(event) {
		if (!menuOpen) return;
		if (event.key === 'Escape') {
			closeMenu();
			return;
		}
		// Keep focus inside the modal menu while it is open
		if (event.key === 'Tab' && menuPanel) {
			const focusables = /** @type {HTMLElement[]} */ ([
				...menuPanel.querySelectorAll('a[href], button, input')
			]);
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement;
			if (event.shiftKey && (active === first || !menuPanel.contains(active))) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		}
	}
</script>

<svelte:window on:keydown={handleKeydown} />

<header class="sticky top-0 z-40 border-b border-stone-200 bg-white/95 backdrop-blur-sm">
	<nav
		class="mx-auto flex h-16 max-w-7xl items-center justify-between gap-x-8 px-6 lg:px-8"
		aria-label="Global"
	>
		<div class="flex shrink-0 items-center gap-x-4">
			<a href="/" class="-m-1.5 p-1.5">
				<span class="sr-only">DCPapers home</span>
				<img
					class="h-6 w-auto sm:h-7"
					src="/images/logos/dcpapers_v01a.svg"
					alt="DCPapers"
					width="762"
					height="86"
				/>
			</a>
			<p class="hidden border-l border-stone-200 pl-4 text-[11px]/4 text-stone-500 xl:block">
				Proceedings of the International Conference<br />
				on Dublin Core and Metadata Applications
			</p>
		</div>

		<div class="hidden lg:flex lg:items-center lg:gap-x-8">
			{#each navItems as item}
				{#if item.label !== 'Search' || (isHomePage && !isSearchPage)}
					<a
						href={item.href}
						aria-current={isActive(item.href, pathname) ? 'page' : undefined}
						class="text-sm font-medium whitespace-nowrap transition-colors {isActive(
							item.href,
							pathname
						)
							? 'text-accent-700'
							: 'text-stone-700 hover:text-accent-700'}"
					>
						{item.label}
					</a>
				{/if}
			{/each}

			{#if !isHomePage && !isSearchPage}
				<form action="/search" method="GET" class="relative">
					<label for="header-search" class="sr-only">Search articles</label>
					<div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
						<svg
							class="size-4 text-stone-400"
							viewBox="0 0 20 20"
							fill="currentColor"
							aria-hidden="true"
						>
							<path
								fill-rule="evenodd"
								d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
								clip-rule="evenodd"
							/>
						</svg>
					</div>
					<input
						id="header-search"
						name="q"
						class="block w-56 rounded-full border border-stone-200 bg-stone-50 py-1.5 pr-3 pl-9 text-sm text-stone-900 placeholder:text-stone-400 focus:border-accent-600 focus:bg-white focus:ring-2 focus:ring-accent-600/20 focus:outline-none"
						placeholder="Search articles"
						type="search"
					/>
				</form>
			{/if}
		</div>

		<div class="flex lg:hidden">
			<button
				type="button"
				class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-stone-700"
				aria-expanded={menuOpen}
				bind:this={menuButton}
				on:click={() => (menuOpen ? closeMenu() : openMenu())}
			>
				<span class="sr-only">Open main menu</span>
				<svg
					class="size-6"
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="1.5"
					stroke="currentColor"
					aria-hidden="true"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
					/>
				</svg>
			</button>
		</div>
	</nav>
</header>

<!-- Deliberately a sibling of <header>, not a child. The header carries
     backdrop-blur, and any backdrop-filter makes an element the containing
     block for its position:fixed descendants — which pinned this dialog to the
     64px header box and let overflow-y-auto clip everything below the logo
     row. Keeping the modal outside the header is also the general rule: a
     fixed overlay must not sit under a filtered or transformed ancestor. -->
<!-- Mobile menu -->
{#if menuOpen}
	<div class="lg:hidden" role="dialog" aria-modal="true" aria-label="Main menu">
		<button
			type="button"
			class="fixed inset-0 z-40 bg-stone-950/25"
			aria-label="Close menu"
			tabindex="-1"
			transition:fade={{ duration: 150 }}
			on:click={closeMenu}
		></button>
		<div
			class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-5 sm:max-w-sm sm:border-l sm:border-stone-200"
			transition:fly={{ x: 320, duration: 200, opacity: 1 }}
			bind:this={menuPanel}
		>
			<div class="flex items-center justify-between">
				<a href="/" class="-m-1.5 p-1.5">
					<span class="sr-only">DCPapers home</span>
					<img class="h-6 w-auto" src="/images/logos/dcpapers_v01a.svg" alt="DCPapers" />
				</a>
				<button type="button" class="-m-2.5 rounded-md p-2.5 text-stone-700" on:click={closeMenu}>
					<span class="sr-only">Close menu</span>
					<svg
						class="size-6"
						fill="none"
						viewBox="0 0 24 24"
						stroke-width="1.5"
						stroke="currentColor"
						aria-hidden="true"
					>
						<path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			<div class="mt-8">
				<form action="/search" method="GET">
					<label for="mobile-search" class="sr-only">Search articles</label>
					<div class="relative">
						<div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
							<svg
								class="size-4 text-stone-400"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fill-rule="evenodd"
									d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
									clip-rule="evenodd"
								/>
							</svg>
						</div>
						<input
							id="mobile-search"
							name="q"
							class="block w-full rounded-full border border-stone-200 bg-stone-50 py-2 pr-3 pl-9 text-sm text-stone-900 placeholder:text-stone-400 focus:border-accent-600 focus:bg-white focus:ring-2 focus:ring-accent-600/20 focus:outline-none"
							placeholder="Search articles"
							type="search"
						/>
					</div>
				</form>
				<div class="mt-6 space-y-1">
					{#each navItems as item}
						<a
							href={item.href}
							aria-current={isActive(item.href, pathname) ? 'page' : undefined}
							class="-mx-3 block rounded-lg px-3 py-2 text-base font-medium {isActive(
								item.href,
								pathname
							)
								? 'bg-accent-50 text-accent-800'
								: 'text-stone-800 hover:bg-stone-50'}"
						>
							{item.label}
						</a>
					{/each}
				</div>
				<p class="mt-8 border-t border-stone-200 pt-6 text-xs/5 text-stone-500">
					Proceedings of the International Conference on Dublin Core and Metadata Applications
					<br />ISSN {CONFIG.conference.issn}
				</p>
			</div>
		</div>
	</div>
{/if}
